import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconGripVertical,
  IconMessages,
  IconThumbtack,
  IconThumbtackSolid,
  IconXmark,
} from '@optiaxiom/icons';
import { Badge, Box, Button, Flex, Text, theme, Tooltip } from '@optiaxiom/react';

import { stickyboardDrawerVisibleAtom } from '@frontend/atoms/stickyboardAtoms';
import type { StickyCard as StickyCardModel } from '@frontend/types/stickyboard';
import { buildMessagesPath } from '@frontend/utils/messagingUrl';

import { useDismissSticky, usePatchSticky, useSaveStickyCardData } from '../hooks/useStickyboard';
import { useStickyboardDnd } from '../hooks/useStickyboardDnd';
import { useTextSelectableDrag } from '../hooks/useTextSelectableDrag';
import { transformChecklist } from '../utils/proteusChecklist';

import { CollapsibleBody } from './CollapsibleBody';
import { ProteusStickyCard } from './ProteusStickyCard';
import { selectableText } from './Stickyboard.css';
import { StickyChecklist } from './StickyChecklist';

/** Initial interactive-card state, lowest → highest precedence: glyph-checklist
 * defaults, then the author's embedded `data`, then the user's saved state. */
function initialCardData(
  sticky: StickyCardModel,
  glyphDefaults: Record<string, boolean>
): Record<string, unknown> {
  const docData = sticky.proteus_card?.data;
  const docDataObj =
    docData && typeof docData === 'object' ? (docData as Record<string, unknown>) : {};
  return { ...glyphDefaults, ...docDataObj, ...(sticky.user_card_data ?? {}) };
}

type Props = {
  sticky: StickyCardModel;
  /** Optional stable rotation (deg) for the post-it feel. */
  rotate?: number;
};

const SENDER_KIND_TONE: Record<string, 'neutral' | 'primary' | 'success'> = {
  vau: 'primary',
  system_agent: 'primary',
  workflow_agent: 'primary',
  user: 'neutral',
  external: 'neutral',
};

/**
 * A single sticky. Renders the sender chip, title, and a text body
 * (body_excerpt). The richer Proteus-card body lands once @optiaxiom/proteus
 * is wired (see ProteusStickyCard); body_excerpt is the always-available
 * fallback and is what the Steward summarises.
 */
export function StickyCard({ sticky, rotate = 0 }: Props) {
  const dismiss = useDismissSticky();
  const patch = usePatchSticky();
  const saveCardData = useSaveStickyCardData();
  const dnd = useStickyboardDnd();
  const navigate = useNavigate();
  const { instanceId } = useParams();
  const setDrawerVisible = useSetAtom(stickyboardDrawerVisibleAtom);

  // When the sticky is also an IM message, deep-link to that conversation so the
  // user can discuss it. Matches the /messages URL contract (see useMessagingUrl).
  const imThreadId =
    sticky.linked_thread_ref?.kind === 'im' ? sticky.linked_thread_ref.thread_id : undefined;
  const handleDiscuss = () => {
    if (!imThreadId) return;
    setDrawerVisible(false);
    const path = buildMessagesPath({
      conversationId: imThreadId,
      threadPanelId: sticky.linked_message_id ?? undefined,
      targetMessageId: sticky.linked_message_id ?? undefined,
    });
    navigate(`/instances/${instanceId}${path}`);
  };

  // Rewrite any text checklist (☐/☑ glyphs) into checkbox Inputs so its boxes
  // become interactive; real toggle cards have no glyphs and pass through. The
  // glyph state seeds the initial checked values.
  const { doc: proteusDoc, initialChecks } = useMemo(
    () => transformChecklist(sticky.proteus_card),
    [sticky.proteus_card]
  );
  // A glyph checklist (Proteus can't draw a real checkbox) → render it ourselves
  // with Axiom Checkbox; everything else goes through Proteus.
  const isChecklist = Object.keys(initialChecks).length > 0;

  // The interactive Proteus card (e.g. a checklist) is a controlled form: its
  // field state lives here and is persisted as the user's own state. Seeded
  // once from the saved state; not re-seeded from props, so an in-flight toggle
  // is never clobbered by a background board refetch.
  const [cardData, setCardData] = useState<Record<string, unknown>>(() =>
    initialCardData(sticky, initialChecks)
  );
  const cardDataRef = useRef(cardData);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stickyId = sticky.sticky_id;
  const { mutate: persistCardData } = saveCardData;

  // Persistence is driven by the change handler (not a render effect), so it
  // only fires on real user edits — never on mount/hydration, and StrictMode's
  // double-invoked effects can't spuriously re-persist. Debounced so a flurry
  // of toggles coalesces into one write; the updater is applied to the latest
  // committed value (ref) so rapid toggles don't clobber each other.
  const handleCardDataChange = useCallback(
    (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => {
      const next = updater(cardDataRef.current);
      cardDataRef.current = next;
      setCardData(next);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persistCardData({ stickyId, data: next }), 500);
    },
    [persistCardData, stickyId]
  );
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    []
  );

  const handleToggle = useCallback(
    (name: string, checked: boolean) =>
      handleCardDataChange(prev => ({ ...prev, [name]: checked })),
    [handleCardDataChange]
  );

  const tone = SENDER_KIND_TONE[sticky.sender_display.kind] ?? 'neutral';
  const pinned = sticky.user_pinned;

  // Keep the card draggable, but let a mouse press on the title/body text run a
  // native text selection instead of starting a drag (see the hook). Text
  // regions are tagged with SELECTABLE_TEXT_ATTR below.
  const selectableDrag = useTextSelectableDrag();

  return (
    <Box
      data-testid="sticky-card"
      draggable={selectableDrag.draggable}
      aria-roledescription="Draggable card"
      onMouseDown={selectableDrag.onMouseDown}
      // Drag the whole card to move it between lanes/stacks — but never start a
      // drag from an interactive control (pin/dismiss, "Show more", a checklist
      // switch), so those keep working.
      onDragStart={e => {
        if (
          (e.target as HTMLElement).closest(
            'button, input, a, [role="switch"], [contenteditable="true"]'
          )
        ) {
          e.preventDefault();
          return;
        }
        dnd.startCardDrag(sticky)(e);
      }}
      onDragEnd={dnd.endDrag}
      // Drop another card here to stack the two (the Steward names the stack).
      onDragOver={dnd.allowDrop}
      onDragLeave={dnd.leaveDrop}
      onDrop={dnd.dropOnCard(sticky)}
      style={{
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        background: theme.colors['bg.default'],
        border: `1px solid ${theme.colors['border.tertiary']}`,
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        padding: 12,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Flex flexDirection="row" gap="4" alignItems="center" justifyContent="space-between">
        <Flex flexDirection="row" gap="4" alignItems="center" style={{ minWidth: 0 }}>
          <Box
            data-testid="card-drag-handle"
            aria-label="Drag to move card"
            style={{
              display: 'flex',
              alignItems: 'center',
              color: theme.colors['fg.tertiary'],
              cursor: 'grab',
            }}
          >
            <IconGripVertical />
          </Box>
          <Badge intent={tone}>{sticky.sender_display.name}</Badge>
        </Flex>
        {/* Card actions live top-right as icons — kept clear of the body's
            "Show more" toggle so the two can't be mis-tapped for each other. */}
        <Flex flexDirection="row" gap="2" alignItems="center">
          {imThreadId && (
            <Tooltip content="Discuss in thread" delayDuration={0}>
              <Button
                size="sm"
                appearance="subtle"
                aria-label="Discuss in thread"
                icon={<IconMessages />}
                onClick={handleDiscuss}
              />
            </Tooltip>
          )}
          <Tooltip content={pinned ? 'Unpin' : 'Pin'} delayDuration={0}>
            <Button
              size="sm"
              appearance="subtle"
              aria-label={pinned ? 'Unpin' : 'Pin'}
              icon={pinned ? <IconThumbtackSolid /> : <IconThumbtack />}
              onClick={() =>
                patch.mutate({ stickyId: sticky.sticky_id, payload: { user_pinned: !pinned } })
              }
              disabled={patch.isPending}
            />
          </Tooltip>
          <Tooltip content="Dismiss" delayDuration={0}>
            <Button
              size="sm"
              appearance="subtle"
              aria-label="Dismiss"
              icon={<IconXmark />}
              onClick={() => dismiss.mutate(sticky.sticky_id)}
              disabled={dismiss.isPending}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Text
        fontWeight="600"
        className={selectableText}
        style={{ marginTop: 8 }}
        {...selectableDrag.textProps}
      >
        {sticky.title}
      </Text>
      <CollapsibleBody>
        {sticky.proteus_card ? (
          <Box style={{ marginTop: 4 }}>
            {isChecklist ? (
              <StickyChecklist doc={proteusDoc} data={cardData} onToggle={handleToggle} />
            ) : (
              <ProteusStickyCard
                card={proteusDoc as Record<string, unknown>}
                data={cardData}
                onDataChange={handleCardDataChange}
                fallback={
                  sticky.body_excerpt ? (
                    <Text
                      fontSize="sm"
                      color="fg.tertiary"
                      className={selectableText}
                      {...selectableDrag.textProps}
                    >
                      {sticky.body_excerpt}
                    </Text>
                  ) : null
                }
              />
            )}
          </Box>
        ) : (
          sticky.body_excerpt && (
            <Text
              fontSize="sm"
              color="fg.tertiary"
              className={selectableText}
              style={{ marginTop: 4 }}
              {...selectableDrag.textProps}
            >
              {sticky.body_excerpt}
            </Text>
          )
        )}
      </CollapsibleBody>
    </Box>
  );
}
